import { HardwareIntegrationInput } from "@/types/system-setting/hardware-integration";
import CustomCollapsible from "@/components/ui/collapsibleCard/collapsible-card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetFieldState,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { Product } from "@/types/system-setting/product";
import { status } from "@/lib/constants/global";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { useGetIncomeCategorListQuery } from "@/services/incomeCategoryApi";
import { useEffect, useState } from "react";
import { useGetSalesTaxListQuery } from "@/services/salesTaxApi";

interface HardwareDetailsSectionProp {
  control: Control<Product>;
  errors: FieldErrors<Product>;
  watch: UseFormWatch<Product>;
  setValue: UseFormSetValue<Product>;
  getValues: UseFormGetFieldState<Product>;
  register: UseFormRegister<Product>;
}
const ProductTypeEnum = {
  wallet: "wallet",
  other_product: "other_product",
};


const ProductPriceSection = ({
  control,
  errors,
  watch,
  setValue,
  getValues,
  register,
}: HardwareDetailsSectionProp) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { data: incomeCategoryData } = useGetIncomeCategorListQuery(orgId);
  const { data: salesTaxData } = useGetSalesTaxListQuery(orgId);

  const price = watch("price") || 0;
  const incomeCategory = watch("income_category_id");
  const [salesTaxName, setSalesTaxName] = useState<string | null>(null);
  const [saleTaxId, setSaleTaxId] = useState<number | null>(null);
  const [salesTaxPercentage, setSalesTaxPercentage] = useState<number>(0);
  const [taxAmount, setTaxAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);

  // Update tax ID based on selected income category
  useEffect(() => {
    if (incomeCategoryData && incomeCategory) {
      const selectedIncomeCategory = incomeCategoryData.find(
        (item) => item.id === Number(incomeCategory)
      );
      console.log("selectedIncomeCategory",selectedIncomeCategory?.sale_tax_id )
      setSaleTaxId(selectedIncomeCategory?.sale_tax_id || null);
      setValue("sale_tax_id",selectedIncomeCategory?.sale_tax_id as number)
    }
  }, [incomeCategory, incomeCategoryData]);

  // Update tax rate and total amount when tax ID or price changes
  useEffect(() => {
    if (salesTaxData && saleTaxId !== null) {
      const salesTax = salesTaxData.find((tax) => tax.id === saleTaxId);
      const taxRate = salesTax?.percentage || 0;
      setSalesTaxPercentage(taxRate);
      setSalesTaxName(salesTax?.name || "N/A"); // Set tax name
      // Calculate tax amount and total price
      const calculatedTaxAmount = (taxRate / 100) * price;
      setTaxAmount(calculatedTaxAmount);

      const finalAmount = Number(price) + Number(calculatedTaxAmount);

      // Ensure `totalAmount` is always a number
      setTotalAmount(Number(finalAmount) || 0);
    }
  }, [saleTaxId, salesTaxData, price]);

 const watcher=watch();

 console.log(watcher)
  return (
    <div className="rounded-2xl bg-white">
      <CustomCollapsible
        title="Price Details"
        isOpenDefault={true}
        hideToggle={false}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium mb-1.5">
                Flexible Price <span className="text-red-500">*</span>
              </p>
              <Controller
                control={control}
                name="flexible_price"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>

            {!watch("flexible_price") && (
              <div>
                <p className="text-sm font-medium mb-1.5">
                  Price <span className="text-red-500">*</span>
                </p>
              <Controller
                control={control}
                name="price"
                rules={{
                  required: "Required",
                  pattern: {
                    value: /^\d+$/,
                    message: "Only numbers are allowed",
                  },
                }}
                render={({ field }) => (
                  <Input
                    className="w-full h-9"
                    placeholder="Enter Product Price"
                    {...field}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) {
                        field.onChange(e);
                      }
                    }}
                  />
                )}
              />
              {errors.price?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.price.message.toString()}
                </p>
              )}
              </div>
            )}
            <div>
              <p className="text-sm font-medium mb-1.5">
                Product Type <span className="text-red-500">*</span>
              </p>
            <Controller
              control={control}
              name="type"
              rules={{ required: "Required" }}
              defaultValue={watcher.type}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Product Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ProductTypeEnum).map(([key, value]) => (
                      <SelectItem key={key} value={value}>
                        {" "}
                        {value
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
              {errors.type?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.type.message.toString()}
                </p>
              )}
            </div>
          </div>

          {watcher.type === ProductTypeEnum.other_product && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium mb-1.5">
                  Manage Inventory <span className="text-red-500">*</span>
                </p>
                <Controller
                  control={control}
                  name="manage_inventory"
                  render={({ field }) => (
                    <div className="flex gap-2 h-full items-center">
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                  )}
                />
                {errors.manage_inventory?.message && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.manage_inventory.message.toString()}
                  </p>
                )}
              </div>

              <div>
                <p className="text-sm font-medium mb-1.5">
                  Inventory<span className="text-red-500">*</span>
                </p>
              <Controller
                control={control}
                name="quantity"
                rules={{
                  required: "Required",
                  pattern: {
                    value: /^\d+$/,
                    message: "Only numbers are allowed",
                  },
                }}
                render={({ field }) => (
                  <Input
                    className="w-full h-9"
                    placeholder="Enter Product Quantity"
                    {...field}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) {
                        field.onChange(e);
                      }
                    }}
                  />
                )}
              />
              {errors.quantity?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.quantity.message.toString()}
                </p>
              )}
              </div>

              <div>
                <p className="text-sm font-medium mb-1.5">
                  Cost<span className="text-red-500">*</span>
                </p>
              <Controller
                control={control}
                name="cost"
                rules={{
                  required: "Required",
                  pattern: {
                    value: /^\d+$/,
                    message: "Only numbers are allowed",
                  },
                }}
                render={({ field }) => (
                  <Input
                    className="w-full h-9"
                    placeholder="Enter Product Cost"
                    {...field}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) {
                        field.onChange(e);
                      }
                    }}
                  />
                )}
              />
              {errors.cost?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.cost.message.toString()}
                </p>
              )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
            {/* <p className="text-base font-normal">
              Income Category <span className="text-red-500">*</span>
            </p> */}
            <Controller
              name="income_category_id"
              rules={{ required: "Required" }}
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={(value) => field.onChange(Number(value))}
                  defaultValue={field.value?.toString()}
                >
                  <SelectTrigger floatingLabel="Income Category " text="*">
                    <SelectValue placeholder="Select income category" />
                  </SelectTrigger>
                  <SelectContent>
                    {incomeCategoryData?.map((category) => (
                      <SelectItem
                        key={category.id}
                        value={category.id.toString()}
                      >
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
              {errors.income_category_id?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.income_category_id.message.toString()}
                </p>
              )}
            </div>

            <div>
              <FloatingLabelInput
                label="Sales Tax"
                text="*"
                disabled={true}
                className="w-full"
                value={salesTaxName || "N/A"}
              />
            </div>
            <div>
            {/* <p className="text-base font-normal">
              Status <span className="text-red-500">*</span>
            </p> */}
            <Controller
              name="status"
              rules={{ required: "Required" }}
              control={control}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => {
                const statusLabel = status.filter((r) => r.value == value)[0];
                return (
                  <Select
                    onValueChange={(value) => onChange(value)}
                    defaultValue={value}
                  >
                    <SelectTrigger floatingLabel="Status" text="*">
                      <SelectValue
                        placeholder="Select status"
                        className="text-gray-400"
                      >
                        <span className="flex gap-2 items-center">
                          <span
                            className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                          ></span>
                          <span>{statusLabel?.label}</span>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {status.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
              {errors.status?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.status.message.toString()}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <FloatingLabelInput
                id="tax_rate"
                disabled={true}
                label="Tax/VAT Rate"
                text="*"
                value={salesTaxPercentage ? `${salesTaxPercentage}%` : "N/A"}
              />
            </div>

            <div>
              <FloatingLabelInput
                id="tax_amount"
                disabled={true}
                label="Tax/VAT Amount"
                text="*"
                type="number"
                value={taxAmount.toFixed(2)}
              />
            </div>

            <div>
              <FloatingLabelInput
                id="total_price"
                label="Total Amount"
                text="*"
                disabled={true}
                type="number"
                value={totalAmount ? totalAmount.toFixed(2) : "0.00"}
              />
            </div>
          </div>
        </div>
      </CustomCollapsible>
    </div>
  );
};

export default ProductPriceSection;